import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../redux/store";
import { TenantConfig } from "../../../types/tenantConfig";
import { HintsLink } from "./HintsLink";

/**
 * Withdrawal information (Widerrufsbelehrung) and model withdrawal form for a
 * service contract — the statutory models of Anlage 1 and Anlage 2 zu
 * Art. 246a § 1 Abs. 2 Satz 2 EGBGB, adapted to a subscription (no delivery,
 * no goods). Plan: `docs/plans/kolula-key-subscription-b2c-obligations.md`,
 * PR 3. The final wording is a lawyer point (O3 there).
 *
 * The trader is the tenant, named from the SAME config fields its Impressum
 * reads (`defaults/config/components/tenantImpressum.tsx`): `organizationName`,
 * `legalNotice` and `contact.email`. A value the config does not carry renders
 * as "fehlend" / "missing" — never a placeholder that looks like a real
 * address, because a withdrawal sent to a made-up address is a lost
 * withdrawal.
 *
 * TWIN: the contract confirmation mail carries the same text word for word —
 * `backend/src/core/lib/email/templates/subscription/contract.html` (trader
 * block: `buildWithdrawalTraderBlock` in
 * `backend/src/core/lib/email/subscriptionMailBlocks.ts`). The app and the
 * Lambda cannot share code; change both. The early-start sentence
 * (`book_location.keys.subscription_summary.early_start_request`) is quoted in
 * the same template.
 */

export interface WithdrawalTrader {
  name?: string;
  street?: string;
  cityLine?: string;
  email?: string;
}

const present = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/** The trader's identity as the withdrawal information names it. */
export const resolveWithdrawalTrader = (
  config?: TenantConfig
): WithdrawalTrader => {
  const ln = config?.legalNotice;
  const cityLine = [present(ln?.postalCode), present(ln?.city)]
    .filter(Boolean)
    .join(" ");
  return {
    name: present(config?.organizationName),
    street: present(ln?.street),
    cityLine: cityLine || undefined,
    email: present(config?.contact?.email),
  };
};

const TraderBlock = ({
  trader,
  missing,
  emailLabel,
}: {
  trader: WithdrawalTrader;
  missing: string;
  emailLabel: string;
}) => (
  <p className="pl-4 border-l-2 border-foreground/10">
    {trader.name ?? missing}
    <br />
    {trader.street ?? missing}
    <br />
    {trader.cityLine ?? missing}
    <br />
    {emailLabel}:{" "}
    {trader.email ? (
      <HintsLink href={`mailto:${trader.email}`}>{trader.email}</HintsLink>
    ) : (
      missing
    )}
  </p>
);

const WithdrawalInformation = ({ lang }: { lang: string }) => {
  const { t } = useTranslation();
  const config = useSelector((state: RootState) => state.tenantConfig.config);
  const trader = resolveWithdrawalTrader(config);
  const missing = t("common.missing");

  if (lang === "de") {
    return (
      <div className="min-h-full px-5 text-foreground gap-4 flex flex-col text-base">
        <h1 className="font-semibold text-foreground/90 text-2xl text-center">
          Widerrufsbelehrung
        </h1>

        <h2 className="font-semibold text-xl">Widerrufsrecht</h2>
        <p>
          Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen
          diesen Vertrag zu widerrufen.
        </p>
        <p>
          Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des
          Vertragsabschlusses.
        </p>
        <p>
          Um Ihr Widerrufsrecht auszuüben, müssen Sie uns
        </p>
        <TraderBlock trader={trader} missing={missing} emailLabel="E-Mail" />
        <p>
          mittels einer eindeutigen Erklärung (z. B. ein mit der Post
          versandter Brief oder eine E-Mail) über Ihren Entschluss, diesen
          Vertrag zu widerrufen, informieren. Sie können dafür das unten
          stehende Muster-Widerrufsformular verwenden, das jedoch nicht
          vorgeschrieben ist.
        </p>
        <p>
          Sie können den Widerruf auch in der App über die Schaltfläche
          „Vertrag widerrufen“ erklären.
        </p>
        <p>
          Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung
          über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist
          absenden.
        </p>

        <h2 className="font-semibold text-xl">Folgen des Widerrufs</h2>
        <p>
          Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen,
          die wir von Ihnen erhalten haben, unverzüglich und spätestens binnen
          vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über
          Ihren Widerruf dieses Vertrags bei uns eingegangen ist. Für diese
          Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der
          ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen
          wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden
          Ihnen wegen dieser Rückzahlung Entgelte berechnet.
        </p>
        <p>
          Haben Sie verlangt, dass die Dienstleistungen während der
          Widerrufsfrist beginnen sollen, so haben Sie uns einen angemessenen
          Betrag zu zahlen, der dem Anteil der bis zu dem Zeitpunkt, zu dem Sie
          uns von der Ausübung des Widerrufsrechts hinsichtlich dieses Vertrags
          unterrichten, bereits erbrachten Dienstleistungen im Vergleich zum
          Gesamtumfang der im Vertrag vorgesehenen Dienstleistungen entspricht.
        </p>

        <h2 className="font-semibold text-xl">Muster-Widerrufsformular</h2>
        <p>
          (Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses
          Formular aus und senden Sie es zurück.)
        </p>
        <ul className="list-disc ml-6 flex flex-col gap-2">
          <li>
            An:
            <TraderBlock trader={trader} missing={missing} emailLabel="E-Mail" />
          </li>
          <li>
            Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen
            Vertrag über die Erbringung der folgenden Dienstleistung:
            ____________________
          </li>
          <li>Bestellt am: ____________________</li>
          <li>Name des/der Verbraucher(s): ____________________</li>
          <li>Anschrift des/der Verbraucher(s): ____________________</li>
          <li>
            Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier):
            ____________________
          </li>
          <li>Datum: ____________________</li>
        </ul>
        <p>(*) Unzutreffendes streichen.</p>
      </div>
    );
  }

  return (
    <div className="min-h-full px-5 text-foreground gap-4 flex flex-col text-base">
      <h1 className="font-semibold text-foreground/90 text-2xl text-center">
        Withdrawal information
      </h1>

      <h2 className="font-semibold text-xl">Right of withdrawal</h2>
      <p>
        You have the right to withdraw from this contract within 14 days without
        giving any reason.
      </p>
      <p>
        The withdrawal period will expire after 14 days from the day of the
        conclusion of the contract.
      </p>
      <p>To exercise the right of withdrawal, you must inform us</p>
      <TraderBlock trader={trader} missing={missing} emailLabel="Email" />
      <p>
        of your decision to withdraw from this contract by an unequivocal
        statement (e.g. a letter sent by post or an email). You may use the
        model withdrawal form below, but it is not obligatory.
      </p>
      <p>
        You can also declare your withdrawal in the app using the “Withdraw from
        contract” button.
      </p>
      <p>
        To meet the withdrawal deadline, it is sufficient for you to send your
        communication concerning your exercise of the right of withdrawal before
        the withdrawal period has expired.
      </p>

      <h2 className="font-semibold text-xl">Effects of withdrawal</h2>
      <p>
        If you withdraw from this contract, we shall reimburse to you all
        payments received from you without undue delay and in any event not
        later than 14 days from the day on which we are informed about your
        decision to withdraw from this contract. We will carry out such
        reimbursement using the same means of payment as you used for the
        initial transaction, unless you have expressly agreed otherwise; in any
        event, you will not incur any fees as a result of such reimbursement.
      </p>
      <p>
        If you requested to begin the performance of services during the
        withdrawal period, you shall pay us an amount which is in proportion to
        what has been provided until you have communicated to us your withdrawal
        from this contract, in comparison with the full coverage of the
        contract.
      </p>

      <h2 className="font-semibold text-xl">Model withdrawal form</h2>
      <p>
        (Complete and return this form only if you wish to withdraw from the
        contract.)
      </p>
      <ul className="list-disc ml-6 flex flex-col gap-2">
        <li>
          To:
          <TraderBlock trader={trader} missing={missing} emailLabel="Email" />
        </li>
        <li>
          I/We (*) hereby give notice that I/We (*) withdraw from my/our (*)
          contract for the provision of the following service:
          ____________________
        </li>
        <li>Ordered on: ____________________</li>
        <li>Name of consumer(s): ____________________</li>
        <li>Address of consumer(s): ____________________</li>
        <li>
          Signature of consumer(s) (only if this form is notified on paper):
          ____________________
        </li>
        <li>Date: ____________________</li>
      </ul>
      <p>(*) Delete as appropriate.</p>
    </div>
  );
};

export default WithdrawalInformation;
